import type { User } from 'oidc-client-ts';

import { browserStorage, getUserFromStorage } from './utils';

export interface AuthState {
  /**
   * See [User](https://authts.github.io/oidc-client-ts/classes/User.html) for more details.
   */
  user?: User | null | undefined;
  isLoading: boolean;
  /**
   * True while the user has a valid access token.
   */
  isAuthenticated: boolean;
  error?: Error;
  sessionExpired?: boolean;
}

export const getInitialState = (): AuthState => {
  const user = getUserFromStorage();

  return {
    isLoading: user ? Boolean(user.expired) : true,
    isAuthenticated: user ? !user.expired : false,
    user,
    error: undefined,
    sessionExpired: false,
  };
};

type Action =
  | { type: 'INITIALISED'; user: User | null }
  | { type: 'USER_LOADED'; user: User | null }
  | { type: 'USER_UNLOADED' }
  | { type: 'SESSION_EXPIRED' }
  | { type: 'ERROR'; error: Error };

export const authReducer = (state: AuthState, action: Action): AuthState => {
  switch (action.type) {
    case 'INITIALISED':
    case 'USER_LOADED':
      return {
        ...state,
        user: action.user,
        isLoading: false,
        isAuthenticated: action.user ? !action.user.expired : false,
        error: undefined,
        sessionExpired: false,
      };
    case 'USER_UNLOADED':
      return {
        ...state,
        user: undefined,
        isAuthenticated: false,
      };
    case 'SESSION_EXPIRED':
      browserStorage.setItem('auth:sessionExpired', 'true');
      return {
        ...state,
        sessionExpired: true,
      };
    case 'ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.error,
      };
    default:
      return {
        ...state,
        isLoading: false,
        error: new Error(`unknown action ${action}`),
      };
  }
};
